import { memo, useCallback, useEffect, useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleSection, EventCard } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useOpenInNewWindow } from '@/hooks/useOpenInNewWindow';
import { useSourcesStore } from '@/stores';
import {
    usePipPlayerStore,
    usePipReportEventWatcher,
} from '@/stores/pip-player.store';
import type { ReportEvent } from '@/types/report';
import { getReportEventDisplayConfidence } from '@/utils/report-event-confidence';
import { buildEventCardMetadata } from '@/utils/event-card-metadata';
import { getReportEventTime } from '@/utils/report-event-time';
import { isVideoTimeRange } from '@/utils/date';
import {
    REPORT_GROUP_BY_EXPAND_ALL_MAX_GROUPS,
    type ReportGroupByMode,
} from '../constants';
import { useExportReportEventMenuItem } from '../hooks/useExportReportEventMenuItem';
import type { ReportEventReordering } from '../hooks/useReportEventOrdering';
import type { ReportPromptReordering } from '../hooks/useReportPromptOrdering';
import {
    groupReportEvents,
    type ReportEventGroup,
    REPORT_EVENT_GROUP_EMPTY_KEY,
} from '../utils/groupReportEvents';
import type { ReportEventCardOptions } from './SortableReportEventCard';
import { SortableReportEventsGrid } from './SortableReportEventsGrid';
import { SortableReportPromptGroups } from './SortableReportPromptGroups';

export interface ReportEventsListProps {
    reportId: string;
    events: ReportEvent[];
    groupBy: ReportGroupByMode;
    identitySelectionKey: string;
    onDelete?: (eventId: string) => void;
    onEdit?: (eventId: string) => void;
    onExport?: (event: ReportEvent) => void;
    exportBusy?: boolean;
    isMultiSelectMode?: boolean;
    selectedIds?: Set<string>;
    onToggleSelect?: (eventId: string) => void;
    reordering: ReportEventReordering;
    promptReordering: ReportPromptReordering;
}

function ReportEventsListBase({
    reportId,
    events,
    groupBy,
    identitySelectionKey,
    onDelete,
    onEdit,
    onExport,
    exportBusy = false,
    isMultiSelectMode = false,
    selectedIds,
    onToggleSelect,
    reordering,
    promptReordering,
}: ReportEventsListProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const { openReportEvent } = useOpenInNewWindow();
    const getSourceById = useSourcesStore((state) => state.getSourceById);
    const exportEventMenuItem = useExportReportEventMenuItem();
    const pipOpenReportEvent = usePipPlayerStore((s) => s.openReportEvent);
    const isPipPlaying = usePipReportEventWatcher();
    const refreshPipNavigationContext = usePipPlayerStore(
        (s) => s.refreshNavigationContext,
    );

    const pipNavigationItems = useMemo(
        () => events.map((event) => ({ id: event.id })),
        [events],
    );
    const indexById = useMemo(
        () => new Map(events.map((event, index) => [event.id, index])),
        [events],
    );

    useEffect(() => {
        refreshPipNavigationContext({
            type: 'report-events',
            items: pipNavigationItems,
        });
    }, [pipNavigationItems, refreshPipNavigationContext]);

    const handleEventClick = useCallback(
        (eventId: string, mouseEvent: MouseEvent) => {
            openReportEvent(eventId, mouseEvent, {
                state: {
                    from: 'report-detail',
                    feedContext: { type: 'reportEvents', reportId },
                },
            });
        },
        [openReportEvent, reportId],
    );

    const grouped = useMemo(() => {
        if (groupBy === 'none') {
            return null;
        }
        return groupReportEvents(events, groupBy);
    }, [events, groupBy]);

    if (events.length === 0) {
        return (
            <div className="flex flex-1 items-center justify-center py-16">
                <p className="text-grey-300">{t('detail.noEvents')}</p>
            </div>
        );
    }

    const renderEventCard = (
        event: ReportEvent,
        { dragIndicator, isDragging }: ReportEventCardOptions,
    ) => {
        const confidence = getReportEventDisplayConfidence(event);
        const source = getSourceById(event.sourceId);
        const sourceLabel = event.sourceName ?? source?.displayName ?? '';
        const eventTime = getReportEventTime(event);
        const videoRange =
            eventTime !== undefined && isVideoTimeRange(event.sourceType);
        const metadata = buildEventCardMetadata({
            createdAt: videoRange
                ? event.createdAt
                : (eventTime ?? event.createdAt),
            eventStartTime: eventTime,
            eventEndTime: event.videoEndSeconds ?? undefined,
            isVideoRange: videoRange,
            sourceType: event.sourceType,
            sourceLabel,
            locationName: source?.location?.name,
        });
        return (
            <EventCard
                id={event.id}
                title={event.promptText}
                thumbnailUrls={event.thumbnailUrls}
                confidence={confidence}
                metadata={metadata}
                quickView={{
                    state: isPipPlaying(event.id) ? 'watching' : 'idle',
                    onClick: () =>
                        pipOpenReportEvent({
                            reportEventId: event.id,
                            navigationContext: {
                                type: 'report-events',
                                items: pipNavigationItems,
                                currentIndex: indexById.get(event.id) ?? 0,
                            },
                        }),
                }}
                onClick={handleEventClick}
                extraActions={
                    onExport
                        ? exportEventMenuItem(event, {
                              busy: exportBusy,
                              startExport: onExport,
                          })
                        : undefined
                }
                onEdit={onEdit}
                editLabel={t('detail.editEventDetails')}
                onDelete={onDelete}
                deleteLabel={t('detail.removeFromReport')}
                selectable={isMultiSelectMode}
                selected={selectedIds?.has(event.id) ?? false}
                onSelectToggle={onToggleSelect}
                dragIndicator={dragIndicator}
                state={isDragging ? 'hover' : 'default'}
                className="cursor-grab active:cursor-grabbing"
            />
        );
    };

    const renderSortableGrid = (gridEvents: ReportEvent[]) => (
        <SortableReportEventsGrid
            events={gridEvents}
            disabled={isMultiSelectMode}
            reordering={reordering}
            renderEvent={renderEventCard}
        />
    );

    if (groupBy === 'none' || !grouped) {
        return renderSortableGrid(events);
    }

    const groupSectionHeading = (group: ReportEventGroup) => {
        if (group.key === REPORT_EVENT_GROUP_EMPTY_KEY) {
            return groupBy === 'prompt'
                ? t('detail.groupBy.unknownPrompt')
                : t('detail.groupBy.unknownSource');
        }
        if (group.sectionLabel !== undefined) {
            return group.sectionLabel;
        }
        return group.key;
    };

    const hasFewGroups =
        grouped.length < REPORT_GROUP_BY_EXPAND_ALL_MAX_GROUPS[groupBy];
    const hasIdentitySelection = identitySelectionKey.length > 0;

    const renderGroupSection = (group: ReportEventGroup, index: number) => (
        <CollapsibleSection
            key={`${group.key}:${identitySelectionKey}`}
            label={groupSectionHeading(group)}
            count={group.events.length}
            countAnimated={false}
            defaultOpen={hasFewGroups || hasIdentitySelection || index === 0}
            className="p-0"
        >
            {renderSortableGrid(group.events)}
        </CollapsibleSection>
    );

    if (groupBy === 'prompt') {
        return (
            <SortableReportPromptGroups
                groups={grouped}
                disabled={isMultiSelectMode}
                reordering={promptReordering}
                renderGroup={renderGroupSection}
            />
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {grouped.map((group, index) => renderGroupSection(group, index))}
        </div>
    );
}

ReportEventsListBase.displayName = 'ReportEventsList';
export const ReportEventsList =
    memo<ReportEventsListProps>(ReportEventsListBase);
